import { Controller } from "@hotwired/stimulus"

/**
 * waiver-submit-gate — gates the public waiver wizard's submit button
 * until every requirement on the completion step is satisfied:
 *
 *   - passportInput has a file (or already-attached server-side)
 *   - certificationInput has a file (only when present in DOM, i.e. when
 *     the diver answered "I am a certified diver" in step 3)
 *   - insuranceInput has a file (only when present in DOM, i.e. when the
 *     diver answered "I have diving insurance" in step 4)
 *   - agreementCheckbox is checked
 *   - signatureDate has a value
 *   - typedSignature is non-empty OR signatureImage has a file
 *
 * File inputs honour `data-already-attached="true"` for users who return
 * to this step after the file was uploaded server-side.
 *
 * The controller also drives an inverse toggle on saveExitContainer
 * targets: visible while Submit is disabled, hidden when Submit enables.
 * That gives users a graceful exit when required fields are missing.
 *
 * Pre-completion Save & Exit buttons (outside the saveExitContainer
 * target) are unaffected — the controller is not active on those steps.
 */
export default class extends Controller {
  static targets = [
    "passportInput",
    "certificationInput",
    "insuranceInput",
    "agreementCheckbox",
    "signatureDate",
    "typedSignature",
    "signatureImage",
    "submitButton",
    "saveExitContainer",
  ]

  connect() {
    this.refresh()
  }

  refresh() {
    const enabled = this._allRequirementsMet()
    this.submitButtonTargets.forEach((button) => {
      button.disabled = !enabled
      button.classList.toggle("opacity-50", !enabled)
      button.classList.toggle("cursor-not-allowed", !enabled)
    })
    // Save & Exit is visible when Submit is disabled (the user still has
    // required fields to fill in or attachments to add). Hidden when Submit
    // becomes enabled to keep the primary action focused.
    this.saveExitContainerTargets.forEach((container) => {
      container.classList.toggle("d-none", enabled)
    })
  }

  _allRequirementsMet() {
    if (!this._fileSatisfied(this.hasPassportInputTarget ? this.passportInputTarget : null, true)) return false
    if (!this._fileSatisfied(this.hasCertificationInputTarget ? this.certificationInputTarget : null, false)) return false
    if (!this._fileSatisfied(this.hasInsuranceInputTarget ? this.insuranceInputTarget : null, false)) return false

    if (this.hasAgreementCheckboxTarget && !this.agreementCheckboxTarget.checked) return false

    if (this.hasSignatureDateTarget && this.signatureDateTarget.value.trim() === "") return false

    const typed = this.hasTypedSignatureTarget ? this.typedSignatureTarget.value.trim() : ""
    const drawn = this.hasSignatureImageTarget && this.signatureImageTarget.files && this.signatureImageTarget.files.length > 0
    if (!typed && !drawn) return false

    return true
  }

  // input is the file input element (or null if not in DOM).
  // requiredWhenAbsent: if true, a missing input fails the check.
  // When the input is present, it passes if it has a file OR is already
  // attached server-side (data-already-attached="true").
  _fileSatisfied(input, requiredWhenAbsent) {
    if (!input) return !requiredWhenAbsent
    if (input.files && input.files.length > 0) return true
    if (input.dataset.alreadyAttached === "true") return true
    return false
  }
}
